import { api } from 'app/utils/api/helpers'

const NOTE_TARGET_PM_CONFIRMATION = 24
const NOTE_TARGET_UNDO_PM_CONFIRMATION = 25
export const SMART_DOCS_FIELD_JOB_POLL_INTERVAL_MS = 2000
export const SMART_DOCS_LIST_POLL_INTERVAL_MS = 2000
export const SMART_DOCS_JOB_TERMINAL_STATUS = 400
export const SMART_DOCS_JOB_SUCCESS_STATUS = 500

export const SMART_DOCS_DOC_STATE_CONVERTING = 'converting'
export const SMART_DOCS_DOC_STATE_ANALYZING = 'analyzing'
export const SMART_DOCS_DOC_STATE_READY = 'ready'

const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// fetchSmartDocsAnalysis hits the unified Smart Docs analysis endpoint.
// `agencyID` is mandatory and lives in the URL path. Provide either
// `documentIDs` (renter post-upload, or PM with explicit IDs) or
// `applicantID` (PM applicant view).
export const fetchSmartDocsAnalysis = ({
  agencyID,
  documentIDs,
  applicantID,
} = {}) => {
  const params = {}

  if (Array.isArray(documentIDs) && documentIDs.length > 0) {
    params.document_ids = documentIDs
  }

  if (applicantID) {
    params.applicant_id = applicantID
  }

  return api.get(
    `sm/doc-analysis/agencies/${agencyID}/analysis/smartdocs`,
    Object.keys(params).length ? { params } : undefined,
  )
}

export const fetchAIAnalysisOfDocumentPoller = (jobID) => {
  return api.get(`sm/doc-analysis/${jobID}/status`)
}

export const isSmartDocsJobTerminal = (job = {}) =>
  Number(job?.status) >= SMART_DOCS_JOB_TERMINAL_STATUS

export const isSmartDocsJobSuccess = (job = {}) =>
  Number(job?.status) >= SMART_DOCS_JOB_SUCCESS_STATUS

const hasConvertingDocs = (response = {}) => {
  const states = response?.document_states || {}
  return Object.values(states).some(
    (state) => state === SMART_DOCS_DOC_STATE_CONVERTING,
  )
}

const collectActiveJobIDs = (response = {}) => {
  const jobs = response?.processing_jobs || {}
  return Array.from(new Set(Object.values(jobs).filter(Boolean)))
}

// pollSmartDocsAnalysis is the single Smart Docs poller used by both renter
// (post-upload) and PM (applicant view) flows. It calls the unified list
// endpoint, then either polls each docType's job to terminal and refetches
// the list, or — when documents are still converting — waits and refetches.
//
// Lifecycle:
//   1. Call list endpoint via fetchAnalysis().
//   2. Surface intermediate result via onResult.
//   3. If processing_jobs has active jobs, poll each via pollJob until
//      terminal, then refetch the list.
//   4. Else if any document_states[doc] === 'converting', wait and refetch.
//   5. Else stop and emit final result via onResult.
//
// Returns a function that cancels future scheduled refetches.
export const pollSmartDocsAnalysis = ({
  fetchAnalysis,
  pollJob = fetchAIAnalysisOfDocumentPoller,
  onResult = () => {},
  onError = () => {},
  waitFor: waitForFn = waitFor,
  listPollIntervalMs = SMART_DOCS_LIST_POLL_INTERVAL_MS,
  jobPollIntervalMs = SMART_DOCS_FIELD_JOB_POLL_INTERVAL_MS,
} = {}) => {
  let cancelled = false

  const cancel = () => {
    cancelled = true
  }

  const refetch = () => {
    if (cancelled) {
      return
    }

    fetchAnalysis()
      .then((response) => {
        if (cancelled) {
          return
        }

        onResult(response)

        const jobIDs = collectActiveJobIDs(response)

        if (jobIDs.length > 0) {
          waitForActiveJobs(jobIDs).then(() => {
            if (!cancelled) {
              refetch()
            }
          })
          return
        }

        if (hasConvertingDocs(response)) {
          waitForFn(listPollIntervalMs).then(() => {
            if (!cancelled) {
              refetch()
            }
          })
        }
      })
      .catch((error) => {
        if (!cancelled) {
          onError(error)
        }
      })
  }

  const waitForActiveJobs = (jobIDs) =>
    Promise.all(jobIDs.map(pollUntilTerminal)).catch((error) => {
      if (!cancelled) {
        onError(error)
      }
    })

  const pollUntilTerminal = (jobID) => {
    if (cancelled) {
      return Promise.resolve(null)
    }

    return pollJob(jobID).then((job) => {
      if (cancelled) {
        return null
      }

      if (isSmartDocsJobTerminal(job)) {
        return job
      }

      return waitForFn(jobPollIntervalMs).then(() => pollUntilTerminal(jobID))
    })
  }

  refetch()

  return cancel
}

export const postRenterConfirmation = (documentId) => {
  return api.post(`sm/doc-analysis/analysis/${documentId}/confirm`, {
    confirmation_type: 'renter_confirmed',
  })
}

export const postPMConfirmation = (documentId, applicationGUID, agencyGUID) => {
  return api.post(`sm/doc-analysis/analysis/${documentId}/confirm`, {
    confirmation_type: 'pm_confirmed',
    application_id: applicationGUID,
    agency_id: agencyGUID,
    note_target: NOTE_TARGET_PM_CONFIRMATION,
  })
}

export const undoRenterConfirmation = (documentId, confirmationID) => {
  return api.post(
    `sm/doc-analysis/analysis/${documentId}/confirm/${confirmationID}/undo`,
    {},
  )
}

export const undoPMConfirmation = (
  documentId,
  confirmationID,
  applicationID,
) => {
  return api.post(
    `sm/doc-analysis/analysis/${documentId}/confirm/${confirmationID}/undo`,
    {
      application_id: applicationID,
      note_target: NOTE_TARGET_UNDO_PM_CONFIRMATION,
    },
  )
}

export const fetchCopilotAnalysis = (
  applicationGUID,
  applicantID,
  agencyGUID,
) => {
  return api.get(
    `sm/doc-analysis/agencies/${agencyGUID}/applications/${applicationGUID}/copilot`,
  )
}
